const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// --- 401 / unauthorized handling ------------------------------------------
// useAuth registers a handler here so the API layer can trigger a session
// reset (clear token + re-prompt GIS) without importing React.
let onUnauthorized = null;

/** Register a callback invoked whenever the API gets a 401 from the gateway. */
export function registerUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

/** Thrown for non-2xx responses; carries the HTTP status for callers/tests. */
export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * Centralized fetch wrapper (the "interceptor").
 *
 * - Attaches the Bearer id_token to every request.
 * - Sets JSON Content-Type only when there's a body.
 * - On 401 Unauthorized: clears the session via the registered handler, then throws.
 * - On any other non-2xx: throws an ApiError with the provided error message.
 *
 * @param {string} token           Google id_token (JWT).
 * @param {string} path            Path beginning with '/', appended to BASE_URL.
 * @param {object} [opts]
 * @param {string} [opts.method='GET']
 * @param {*}      [opts.body]      Serialized to JSON when present.
 * @param {object} [opts.query]     Key/value pairs -> query string (skips null/undefined/'').
 * @param {'json'|'blob'|'none'} [opts.responseType='json']
 * @param {string} [opts.errorMessage='Request failed']
 */
async function apiFetch(token, path, opts = {}) {
  const {
    method = 'GET',
    body,
    query,
    responseType = 'json',
    errorMessage = 'Request failed',
  } = opts;

  let url = `${BASE_URL}${path}`;
  if (query) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, v);
    });
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }

  const headers = { 'Authorization': `Bearer ${token}` };
  const init = { method, headers };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  const res = await fetch(url, init);

  if (res.status === 401) {
    // Session no longer valid at the gateway — reset and re-prompt.
    if (onUnauthorized) onUnauthorized();
    throw new ApiError('Unauthorized', 401);
  }

  if (!res.ok) {
    throw new ApiError(errorMessage, res.status);
  }

  if (responseType === 'json') return res.json();
  if (responseType === 'blob') return res.blob();
  return undefined; // 'none'
}

export const api = {
  login(token) {
    return apiFetch(token, '/api/user/login', { method: 'POST', errorMessage: 'Login failed' });
  },

  triggerSync(token) {
    return apiFetch(token, '/api/user/sync', { method: 'POST', errorMessage: 'Sync failed' });
  },

  getBankAccounts(token) {
    return apiFetch(token, '/api/bankaccounts', { errorMessage: 'Failed to fetch bank accounts' });
  },

  createBankAccount(token, data) {
    return apiFetch(token, '/api/bankaccounts', { method: 'POST', body: data, errorMessage: 'Failed to create bank account' });
  },

  updateBankAccount(token, id, data) {
    return apiFetch(token, `/api/bankaccounts/${id}`, { method: 'PUT', body: data, responseType: 'none', errorMessage: 'Failed to update bank account' });
  },

  deleteBankAccount(token, id) {
    return apiFetch(token, `/api/bankaccounts/${id}`, { method: 'DELETE', responseType: 'none', errorMessage: 'Failed to delete bank account' });
  },

  getPaymentRequests(token) {
    return apiFetch(token, '/api/paymentrequests', { errorMessage: 'Failed to fetch payment requests' });
  },

  createPaymentRequest(token, data) {
    return apiFetch(token, '/api/paymentrequests', { method: 'POST', body: data, errorMessage: 'Failed to create payment request' });
  },

  updatePaymentRequest(token, id, data) {
    return apiFetch(token, `/api/paymentrequests/${id}`, { method: 'PUT', body: data, responseType: 'none', errorMessage: 'Failed to update payment request' });
  },

  deletePaymentRequest(token, id) {
    return apiFetch(token, `/api/paymentrequests/${id}`, { method: 'DELETE', responseType: 'none', errorMessage: 'Failed to delete payment request' });
  },

  getAllOpenRequests(token) {
    return apiFetch(token, '/api/admin/open-requests', { errorMessage: 'Failed to fetch open requests' });
  },

  getAppliers(token) {
    return apiFetch(token, '/api/admin/appliers', { errorMessage: 'Failed to fetch appliers' });
  },

  approveRequest(token, requestId, applierId) {
    return apiFetch(token, '/api/admin/approve', { method: 'PUT', body: { requestId, applierId }, responseType: 'none', errorMessage: 'Failed to approve request' });
  },

  searchAppliers(token, q) {
    return apiFetch(token, '/api/admin/appliers/search', { query: { q }, errorMessage: 'Failed to search appliers' });
  },

  getApproved(token, ids) {
    return apiFetch(token, '/api/admin/approved', { query: { ids: ids.join(',') }, errorMessage: 'Failed to fetch approved requests' });
  },

  getPdfExports(token) {
    return apiFetch(token, '/api/admin/pdf-exports', { errorMessage: 'Failed to fetch pdf exports' });
  },

  getPdfExportUrl(token, id) {
    return apiFetch(token, `/api/admin/pdf-exports/${id}/url`, { errorMessage: 'Failed to get pdf url' });
  },

  getUserBankAccounts(token, userId) {
    return apiFetch(token, `/api/admin/user-bank-accounts/${userId}`, { errorMessage: 'Failed to fetch user bank accounts' });
  },

  assignBankAccount(token, sheetRequestId, bankAccountId) {
    return apiFetch(token, '/api/admin/assign-bank-account', { method: 'PUT', body: { sheetRequestId, bankAccountId }, responseType: 'none', errorMessage: 'Failed to assign bank account' });
  },

  exportPdf(token, requestIds) {
    return apiFetch(token, '/api/admin/export-pdf', { method: 'POST', body: { requestIds }, responseType: 'blob', errorMessage: 'Failed to export PDF' });
  },

  getUploadUrl(token, paymentRequestId, fileName, contentType) {
    return apiFetch(token, '/api/attachments/upload-url', { method: 'POST', body: { paymentRequestId, fileName, contentType }, errorMessage: 'Failed to get upload URL' });
  },

  confirmUpload(token, paymentRequestId, s3Key, fileName, contentType) {
    return apiFetch(token, '/api/attachments/confirm', { method: 'POST', body: { paymentRequestId, s3Key, fileName, contentType }, errorMessage: 'Failed to confirm upload' });
  },

  getAttachments(token, paymentRequestId) {
    return apiFetch(token, `/api/attachments/${paymentRequestId}`, { errorMessage: 'Failed to get attachments' });
  },

  getAttachmentsBatch(token, ids) {
    return apiFetch(token, '/api/attachments/batch', { query: { ids: ids.join(',') }, errorMessage: 'Failed to get attachments' });
  },

  deleteAttachment(token, id) {
    return apiFetch(token, `/api/attachments/${id}`, { method: 'DELETE', responseType: 'none', errorMessage: 'Failed to delete attachment' });
  },

  getDownloadUrl(token, s3Key) {
    return apiFetch(token, '/api/attachments/download-url', { query: { s3Key }, errorMessage: 'Failed to get download URL' });
  },

  searchCheckImages(token, { checkNumber, dateFrom, dateTo } = {}) {
    return apiFetch(token, '/api/checkimages/search', { query: { checkNumber, dateFrom, dateTo }, errorMessage: 'Failed to search check images' });
  },

  getPresignedUrl(token, s3Key) {
    return apiFetch(token, '/api/checkimages/presigned-url', { query: { s3Key }, errorMessage: 'Failed to get presigned URL' });
  },

  rejectRequest(token, requestId, reason) {
    return apiFetch(token, '/api/admin/reject', { method: 'PUT', body: { requestId, reason }, responseType: 'none', errorMessage: 'Failed to reject' });
  },

  reopenRequest(token, requestId, reason) {
    return apiFetch(token, '/api/admin/reopen', { method: 'PUT', body: { requestId, reason }, responseType: 'none', errorMessage: 'Failed to reopen' });
  },

  getRequestActions(token, requestId) {
    return apiFetch(token, `/api/admin/request-actions/${requestId}`, { errorMessage: 'Failed to get actions' });
  },

  getHistory(token, { search, status, email, dateFrom, dateTo } = {}) {
    return apiFetch(token, '/api/admin/history', { query: { search, status, email, dateFrom, dateTo }, errorMessage: 'Failed to fetch history' });
  },

  getHistoryStats(token) {
    return apiFetch(token, '/api/admin/history/stats', { errorMessage: 'Failed to fetch stats' });
  },

  getUnpaidChecks(token) {
    return apiFetch(token, '/api/admin/unpaid-checks', { errorMessage: 'Failed to fetch unpaid checks' });
  },

  getAppliersUnpaidChecks(token) {
    return apiFetch(token, '/api/admin/appliers-unpaid-checks', { errorMessage: 'Failed to fetch appliers to review' });
  },

  // --- Money fund (קרן כספית) buy/sell bank letters ---

  generateMoneyFundRequest(token, action, amount) {
    return apiFetch(token, '/api/admin/money-fund/generate', { method: 'POST', body: { action, amount }, responseType: 'blob', errorMessage: 'Failed to generate money fund letter' });
  },

  getMoneyFundRequests(token) {
    return apiFetch(token, '/api/admin/money-fund', { errorMessage: 'Failed to fetch money fund requests' });
  },

  getMoneyFundRequestUrl(token, id) {
    return apiFetch(token, `/api/admin/money-fund/${id}/url`, { errorMessage: 'Failed to get money fund url' });
  },

  deleteMoneyFundRequest(token, id) {
    return apiFetch(token, `/api/admin/money-fund/${id}`, { method: 'DELETE', responseType: 'none', errorMessage: 'Failed to delete money fund request' });
  },

  // --- User & role management (InvoicesUsersDb / ASP.NET Identity) ---

  register(token) {
    return apiFetch(token, '/api/users/register', { method: 'POST', errorMessage: 'Registration failed' });
  },

  getUsers(token) {
    return apiFetch(token, '/api/users', { errorMessage: 'Failed to fetch users' });
  },

  getManageableRoles(token) {
    return apiFetch(token, '/api/users/roles', { errorMessage: 'Failed to fetch roles' });
  },

  setUserRoles(token, userId, roles) {
    return apiFetch(token, `/api/users/${userId}/roles`, { method: 'PUT', body: { roles }, responseType: 'none', errorMessage: 'Failed to update roles' });
  },
};
